/**
 * PLAT-10915 — the surface-side half of AI text generation.
 *
 * Sits between the surfaces (the clip-preview transcript bridge and the canvas
 * actions layer) and `generateCompositionAIText`. It owns the three things the
 * pure service can't:
 *
 *  - resolving what a graphic reads, through `resolveAITextBaseText` — the same
 *    definition the control previews, so a generation can't read something
 *    other than what the creator was shown;
 *  - the per-control in-flight / error state the control renders. Keyed by
 *    element + control and held for the session, so the failure of a generation
 *    fired at INSERT is still there with its retry when the creator opens quick
 *    edit a minute later (DS3 — no silent failure);
 *  - firing the automatic first generation for a freshly created composition.
 *
 * Nothing here is surface-specific: both entry points into the clip preview
 * (transcript chip and canvas Edit pill) build it the same way, so the same
 * graphic behaves identically whichever one the creator used.
 */
import {
  trackGraphicsAiTextGenerationCompleted,
  trackGraphicsAiTextGenerationFailed,
  trackGraphicsAiTextGenerationStarted,
  trackGraphicsAiTextOptionSelected,
} from '@src/analytics/EventTracker';
import { IDLE_AI_TEXT_STATUS } from '@src/components/sidepanel/CompositionSettings/types';
import { useAIPromptRunLog } from '@src/monitoring/hooks/useAIPromptRunLog';
import {
  generateAIText,
  PROMPT_GENERATE_ENDPOINT,
} from '@src/repositories/AITextGenerationRepository';
import {
  generateCompositionAIText,
  selectCompositionAITextOption,
} from '@src/services/composition/aiTextGeneration';
import { extractErrorInfo } from '@src/utility/error';
import {
  getAiTextProvenance,
  getElementTemplateId,
} from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useMemo, useRef, useState } from 'react';

import { resolveAITextBaseText } from './useAITextBaseText';

import type { AITextTranscriptElement } from './useAITextBaseText';
import type {
  AITextControlSurface,
  GraphicsAITextIdentity,
} from '@src/analytics/EventTracker';
import type {
  AITextBaseTextScope,
  AITextControlConfiguration,
  AITextControlStatus,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';
import type { AITextGenerationTarget } from '@src/services/composition/aiTextGeneration';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type {
  AITextProvenance,
  VideoLike,
} from '@src/utility/extraElementData';

const logger = LogManager.getLogger('hook:useAITextGeneration');

/**
 * PLAT-10917 DS5 — the newest run claimed for each `elementId::controlId`.
 *
 * MODULE level, deliberately. The two clip-preview entry points build their own
 * `useAITextGeneration` instance (separate component trees), so a token held in
 * hook state would not see the other surface's run — and the race this guards
 * is exactly a cross-surface one: the insert-time generation fired by the
 * transcript bridge is still in flight while the canvas Edit pill, whose
 * Generate is not disabled by the other instance's status, starts a second.
 *
 * Never cleared. Entries are two numbers keyed by a short string, one per
 * control the session generated for, and a stale entry is harmless: a new run
 * claims `latest + 1` and is by definition the newest.
 *
 * Two consequences of never clearing, both fine but neither obvious:
 *
 * - The key is `elementId::controlId`, with no story in it. Element ids are
 *   unique within a video and a second story means a second tab (a separate JS
 *   realm, so a separate map), so the narrower key never collides.
 * - Tests get their isolation from the counter being MONOTONIC, not from a
 *   reset hook: whatever a previous test left behind, the next `claimRunToken`
 *   still returns the highest token for that key. A test that needs to force a
 *   specific token would have to reach in here.
 */
const latestRunTokens = new Map<string, number>();

function claimRunToken(key: string): number {
  const token = (latestRunTokens.get(key) ?? 0) + 1;
  latestRunTokens.set(key, token);
  return token;
}

function isSupersededRun(key: string, token: number): boolean {
  return latestRunTokens.get(key) !== token;
}

/**
 * The parts of a `CompositionQuickEditField` a generation addresses.
 * Structural rather than the type itself, so this hook stays independent of
 * the quick-edit extraction module.
 */
export interface AITextGeneratableField {
  controlId: string;
  property: string;
  elementPath: ReadonlyArray<number>;
  aiText?: { promptId: string };
}

export interface UseAITextGenerationParams {
  /**
   * PLAT-10923 — which surface this instance belongs to. REQUIRED, not
   * defaulted: the whole reason the epic's analytics is one ticket rather than
   * one per surface is that every event carries this, and a default would let a
   * new surface report itself as an existing one — the failure that silently
   * corrupts the comparison the property exists to make.
   */
  surface: AITextControlSurface;
  editorStore: EditorStoreLike | null;
  /** Live current video — never cached (the provenance write reads it late). */
  getCurrentVideo: () => VideoLike;
  /** Immediate repaint after the write-back; `markDirty` sync is debounced. */
  refreshSource: () => void;
  /** Context BY REFERENCE — the backend resolves the Brand Kit from it (E3). */
  context: { storyId?: string; showcaseId?: string; clipId?: string };
  /** The clip's transcript, for `selection` and `clip` alike. */
  /**
   * The clip's transcript, for `selection` and `clip` alike.
   *
   * A **getter** is accepted as well as a value, for callers whose transcript has
   * no cached accessor: `VideoEditor.getFullTranscript()` walks every word and is
   * uncached, so a surface that would have to call it on every render passes the
   * function instead and it runs only when a generation actually starts. Keep the
   * function identity stable (`useCallback`) — it is a dependency of the run.
   */
  transcriptElements:
    | ReadonlyArray<AITextTranscriptElement>
    | null
    | undefined
    | (() => ReadonlyArray<AITextTranscriptElement> | null | undefined);
}

export interface AITextGeneration {
  /**
   * The automatic first generation (DS1/DS3). Wired to
   * `createCompositionElement`'s `onAITextControlsCreated`; returns
   * immediately, resolving nothing the creation path waits on.
   */
  generateForNewComposition: (params: {
    elementId: string;
    controls: ReadonlyArray<AITextControlConfiguration>;
    /** The just-inserted graphic, as the marker `findOverlayRanges` maps from. */
    overlay: TimelineMarker | null;
  }) => void;
  /**
   * An explicit run from the control — the DS3 retry — addressed by the
   * quick-edit ROW that asked. Both surfaces hand the popover a `generate`
   * callback and had derived the generation target from the field
   * identically; that derivation lives here instead.
   *
   * Resolves with the text written to the element so the caller can reflect it
   * in an open editor, and with `null` when the generation failed (the reason
   * is in the status).
   */
  generateForField: (params: {
    elementId: string;
    field: AITextGeneratableField;
    baseTextScope: AITextBaseTextScope;
    overlay: TimelineMarker | null;
    instructions?: string;
  }) => Promise<string | null>;
  /** In-flight / error state for one control on one graphic. */
  getStatus: (elementId: string, controlId: string) => AITextControlStatus;
  /**
   * PLAT-10916 — what the last generation left behind for one control, read
   * LIVE off the current video. Deliberately not cached: the blob is mutated
   * in place by a generation and by a selection, and a snapshot taken when the
   * popover mounted would show an empty picker right after Generate resolves.
   */
  getProvenance: (
    elementId: string,
    controlId: string,
  ) => AITextProvenance | undefined;
  /**
   * PLAT-10916 — apply one of the already-generated options to the row's
   * element. Synchronous and network-free (DS3); returns the text written, or
   * `null` when the option or the graphic is gone.
   */
  selectOptionForField: (params: {
    elementId: string;
    field: AITextGeneratableField;
    optionIndex: number;
  }) => string | null;
  /**
   * PLAT-10923 — the analytics identity for one control on one graphic, built
   * here because this is the only place that knows all five parts at once (the
   * surface, the story, and the template behind the element). Handed to
   * `AITextControl` for the two events it owns, so the identity has one
   * construction site rather than one per surface (DS4).
   */
  analytics: (elementId: string, controlId: string) => GraphicsAITextIdentity;
}

export function useAITextGeneration({
  surface,
  editorStore,
  getCurrentVideo,
  refreshSource,
  context,
  transcriptElements,
}: UseAITextGenerationParams): AITextGeneration {
  const [statuses, setStatuses] = useState<Record<string, AITextControlStatus>>(
    {},
  );

  const setStatus = useCallback((key: string, next: AITextControlStatus) => {
    setStatuses(prev => ({ ...prev, [key]: next }));
  }, []);

  /**
   * PLAT-11207 — every run leaves an AI Prompt Log record. It wraps the
   * injected repository call rather than `generateCompositionAIText`, so the
   * recorded latency is the generation and not the write-back that follows it
   * (DS5). The control names only a prompt id, so the record learns the
   * prompt's title and type from the endpoint's own response (DS4).
   */
  const logPromptRun = useAIPromptRunLog();
  const generateAndLog = useCallback<typeof generateAIText>(
    request =>
      logPromptRun(
        {
          aiPromptId: request.promptId,
          endpoint: PROMPT_GENERATE_ENDPOINT,
          storyId: request.storyId,
          showcaseId: request.showcaseId,
          clipId: request.clipId,
        },
        describePrompt =>
          generateAIText({ ...request, onPromptMeta: describePrompt }),
      ),
    [logPromptRun],
  );

  /**
   * PLAT-10917 DS5 — the newest run THIS instance started per control, which is
   * a different question from the newest run overall (see the two-token note in
   * `run`). Status is per instance, so only a newer run from this instance may
   * overwrite what this one put there.
   */
  const ownRunTokens = useRef(new Map<string, number>());
  const ownsStatus = useCallback(
    (key: string, token: number) => ownRunTokens.current.get(key) === token,
    [],
  );

  const analytics = useCallback(
    (elementId: string, controlId: string): GraphicsAITextIdentity => ({
      story_id: context.storyId,
      surface,
      composition_element_id: elementId,
      composition_id: getElementTemplateId(getCurrentVideo(), elementId),
      control_id: controlId,
    }),
    [context.storyId, surface, getCurrentVideo],
  );

  const run = useCallback(
    async (params: {
      elementId: string;
      target: Omit<AITextGenerationTarget, 'elementId'>;
      baseTextScope: AITextBaseTextScope;
      overlay: TimelineMarker | null;
      instructions?: string;
      /**
       * PLAT-10923 — automatic (the composition was just added, E12) or the
       * creator pressing Regenerate. Separated because the insert-time
       * generation would otherwise swamp the manual ones in the data and
       * neither number would mean anything.
       */
      trigger: 'insert' | 'manual';
    }): Promise<string | null> => {
      const {
        elementId,
        target,
        baseTextScope,
        overlay,
        instructions,
        trigger,
      } = params;
      if (!editorStore) return null;

      const key = statusKey(elementId, target.controlId);
      // PLAT-10917 DS5 — claimed BEFORE the await, so a run started while this
      // one is in flight supersedes it.
      const runToken = claimRunToken(key);
      ownRunTokens.current.set(key, runToken);
      setStatus(key, { isGenerating: true, error: null });

      const baseText = resolveAITextBaseText({
        scope: baseTextScope,
        overlay,
        transcriptElements:
          typeof transcriptElements === 'function'
            ? transcriptElements()
            : transcriptElements,
      });

      // PLAT-10923 — the triplet fires from HERE, the one hook every surface
      // builds, so an event is never doubled by a second adapter and never
      // missing from a surface someone forgot to instrument.
      const identity = analytics(elementId, target.controlId);
      const eventBase = { ...identity, base_text: baseTextScope };
      // Read BEFORE the run replaces it: a control that already has a
      // generation record is being re-run, whatever fired it.
      const isRerun =
        getAiTextProvenance(getCurrentVideo(), elementId, target.controlId) !==
        undefined;
      const startedAt = Date.now();
      trackGraphicsAiTextGenerationStarted({
        ...eventBase,
        trigger,
        is_rerun: isRerun,
        has_instructions: Boolean(instructions),
      });

      // The service returns a Result rather than throwing, but it is not the
      // only thing that can fail in here — `getCurrentVideo`, the write-back
      // and the provenance upsert all run inside it. An escaped throw would
      // leave the status stuck at `isGenerating`, and Generate is disabled
      // while generating, so the control would lose its retry for good.
      let result: Awaited<ReturnType<typeof generateCompositionAIText>>;
      try {
        result = await generateCompositionAIText({
          target: { ...target, elementId },
          baseTextScope,
          baseText: baseText.text,
          ...(instructions ? { instructions } : {}),
          context,
          editorStore,
          getCurrentVideo,
          refreshSource,
          generate: generateAndLog,
          isSuperseded: () => isSupersededRun(key, runToken),
        });
      } catch (error) {
        const errorInfo = extractErrorInfo(error);
        logger.error('AI text generation threw', {
          elementId,
          controlId: target.controlId,
          ...errorInfo,
        });
        // Fires even if this run was superseded, unlike the Result path below.
        // A throw is a defect in our own code (the write-back, the provenance
        // upsert), and one that a race happened to overlap is still a defect —
        // suppressing it would hide the bugs this event exists to surface.
        trackGraphicsAiTextGenerationFailed({
          ...eventBase,
          error_message: errorInfo.errorMessage || 'AI text generation failed',
        });
        if (ownsStatus(key, runToken)) {
          setStatus(key, {
            isGenerating: false,
            error: errorInfo.errorMessage || 'AI text generation failed',
          });
        }
        return null;
      }

      // Two different questions, two different tokens.
      //
      // The GLOBAL token decides whether this run's result counts: the service
      // has already refused to write for a superseded run, and the caller must
      // not seed its editor with text that is not on the canvas.
      //
      // The PER-INSTANCE token decides who owns the status. This instance set
      // `isGenerating` when the run started, and only a newer run FROM THIS
      // INSTANCE can take that over. Deferring to the global token here would
      // strand the spinner: when the run that superseded this one belongs to
      // the OTHER surface, it writes to its own status map and never clears
      // this one — leaving a control stuck on "Generating…" with no retry for
      // the rest of the session.
      const superseded = isSupersededRun(key, runToken);
      /*
         PLAT-10923 — the other two thirds of the triplet, on every exit path
         except one: a SUPERSEDED run fires neither. It failed at nothing and
         completed nothing, so a Started with no sibling is the honest record of
         a discarded race (PLAT-10917 DS5) — documented on the tracker so the
         Started surplus reads as intentional rather than as hung generations.
      */
      if (!superseded) {
        if (!result.ok) {
          trackGraphicsAiTextGenerationFailed({
            ...eventBase,
            error_message: result.error.message,
          });
        } else {
          trackGraphicsAiTextGenerationCompleted({
            ...eventBase,
            character_count: result.value.text.length,
            option_count: result.value.options.length,
            duration_ms: Date.now() - startedAt,
          });
        }
      }
      if (ownsStatus(key, runToken)) {
        setStatus(
          key,
          !superseded && !result.ok
            ? { isGenerating: false, error: result.error.message }
            : // A superseded run stands down quietly — its error, if it had
              // one, is about a generation the creator already replaced.
              IDLE_AI_TEXT_STATUS,
        );
      }
      if (superseded || !result.ok) return null;
      return result.value.text;
    },
    [
      editorStore,
      transcriptElements,
      context,
      getCurrentVideo,
      refreshSource,
      setStatus,
      ownsStatus,
      generateAndLog,
      analytics,
    ],
  );

  const generateForField = useCallback(
    (params: {
      elementId: string;
      field: AITextGeneratableField;
      baseTextScope: AITextBaseTextScope;
      overlay: TimelineMarker | null;
      instructions?: string;
    }) => {
      const { elementId, field, baseTextScope, overlay, instructions } = params;
      return run({
        elementId,
        target: {
          controlId: field.controlId,
          promptId: field.aiText?.promptId ?? '',
          property: field.property,
          elementPath: field.elementPath,
        },
        baseTextScope,
        overlay,
        ...(instructions ? { instructions } : {}),
        trigger: 'manual',
      });
    },
    [run],
  );

  const generateForNewComposition = useCallback(
    (params: {
      elementId: string;
      controls: ReadonlyArray<AITextControlConfiguration>;
      overlay: TimelineMarker | null;
    }) => {
      for (const control of params.controls) {
        const elementPath = control.elementPaths?.[0];
        if (!elementPath || !control.id) continue;
        // Scope comes from the control's config, not from how the composition
        // was added (DS2) — the same card must read the same thing whichever
        // button inserted it.
        void run({
          elementId: params.elementId,
          target: {
            controlId: control.id,
            promptId: control.promptId,
            property: control.property,
            elementPath,
          },
          baseTextScope: control.defaultBaseTextScope ?? 'selection',
          overlay: params.overlay,
          trigger: 'insert',
        }).catch(error => {
          // `run` already records the failure on the control; this only guards
          // the fire-and-forget call itself from an unhandled rejection.
          logger.warn('AI text generation rejected', {
            elementId: params.elementId,
            controlId: control.id,
            ...extractErrorInfo(error),
          });
        });
      }
    },
    [run],
  );

  const getStatus = useCallback(
    (elementId: string, controlId: string) =>
      statuses[statusKey(elementId, controlId)] ?? IDLE_AI_TEXT_STATUS,
    [statuses],
  );

  const getProvenance = useCallback(
    (elementId: string, controlId: string) =>
      getAiTextProvenance(getCurrentVideo(), elementId, controlId),
    [getCurrentVideo],
  );

  const selectOptionForField = useCallback(
    (params: {
      elementId: string;
      field: AITextGeneratableField;
      optionIndex: number;
    }): string | null => {
      if (!editorStore) return null;
      const { elementId, field, optionIndex } = params;
      // Read before the write so the scope reported is the one the generation
      // being picked from actually read (the selection doesn't change it).
      const scope = getAiTextProvenance(
        getCurrentVideo(),
        elementId,
        field.controlId,
      )?.baseTextScope;
      const result = selectCompositionAITextOption({
        target: {
          elementId,
          controlId: field.controlId,
          property: field.property,
          elementPath: field.elementPath,
        },
        optionIndex,
        editorStore,
        getCurrentVideo,
        refreshSource,
      });
      // Selecting has no spinner and no error slot of its own — the failure
      // modes are "the graphic went away" and "the blob says something the
      // picker didn't". The service logs each of them at the point of detail;
      // here the picker simply doesn't move.
      if (!result.ok) return null;
      // PLAT-10923 — only on a pick that landed. A failed one wrote nothing to
      // the element, so counting it would overstate how often the alternatives
      // are used, which is the number this event exists to give.
      trackGraphicsAiTextOptionSelected({
        ...analytics(elementId, field.controlId),
        base_text: scope ?? 'selection',
        option_index: optionIndex,
        character_count: result.value.length,
      });
      return result.value;
    },
    [editorStore, getCurrentVideo, refreshSource, analytics],
  );

  return useMemo(
    () => ({
      generateForNewComposition,
      generateForField,
      getStatus,
      getProvenance,
      selectOptionForField,
      analytics,
    }),
    [
      generateForNewComposition,
      generateForField,
      getStatus,
      getProvenance,
      selectOptionForField,
      analytics,
    ],
  );
}

function statusKey(elementId: string, controlId: string): string {
  return `${elementId}::${controlId}`;
}
